import { useMemo, useState } from "react";
import {
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

export default function DualListSelect({
  options = [],
  value = [],
  onChange,
  titleLeft = "Disponíveis",
  titleRight = "Selecionados",
}) {
  const [filterLeft, setFilterLeft] = useState("");
  const [filterRight, setFilterRight] = useState("");
  const [pickedLeft, setPickedLeft] = useState([]);
  const [pickedRight, setPickedRight] = useState([]);

  // 🔧 SEMPRE trabalhar com strings
  const selectedSet = useMemo(
    () => new Set((value || []).map((v) => String(v))),
    [value]
  );

  const normalizedOptions = useMemo(() => {
    let arr = options;
    if (!arr) return [];
    if (!Array.isArray(arr) && typeof arr === "object")
      arr = Object.values(arr);
    if (!Array.isArray(arr)) return [];

    return arr
      .filter(Boolean)
      .map((o) => ({
        id: String(o.id ?? o.value ?? o.key), // 🔧 força string
        name: o.name ?? o.label ?? String(o.id ?? o.value ?? o.key ?? ""),
      }))
      .filter((o) => o.id != null && o.name);
  }, [options]);

  const leftList = useMemo(() => {
    return normalizedOptions
      .filter((opt) => !selectedSet.has(opt.id))
      .filter((opt) =>
        filterLeft
          ? opt.name.toLowerCase().includes(filterLeft.toLowerCase())
          : true
      );
  }, [normalizedOptions, selectedSet, filterLeft]);

  const rightList = useMemo(() => {
    return normalizedOptions
      .filter((opt) => selectedSet.has(opt.id))
      .filter((opt) =>
        filterRight
          ? opt.name.toLowerCase().includes(filterRight.toLowerCase())
          : true
      );
  }, [normalizedOptions, selectedSet, filterRight]);

  // mover itens
  const moveRight = () => {
    if (!pickedLeft.length) return;
    const base = (value || []).map(String);
    const add = pickedLeft.map(String);
    const newValue = Array.from(new Set([...base, ...add])); // 🔧 strings
    onChange?.(newValue);
    setPickedLeft([]);
  };

  const moveLeft = () => {
    if (!pickedRight.length) return;
    const removeSet = new Set(pickedRight.map(String));
    const newValue = (value || [])
      .map(String)
      .filter((id) => !removeSet.has(id));
    onChange?.(newValue);
    setPickedRight([]);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Esquerda */}
      <div>
        <div className="mb-2 font-medium text-sm text-gray-700">
          {titleLeft}
        </div>
        <input
          className="w-full mb-2 border rounded px-2 py-1"
          placeholder="Filtrar..."
          value={filterLeft}
          onChange={(e) => setFilterLeft(e.target.value)}
        />
        <select
          multiple
          className="w-full h-48 border rounded p-2 bg-white"
          value={pickedLeft}
          onChange={
            (e) =>
              setPickedLeft(
                Array.from(e.target.selectedOptions, (o) => String(o.value))
              ) // 🔧 string
          }
        >
          {leftList.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </select>
      </div>

      {/* Centro */}
      <div className="flex flex-col items-center justify-center gap-2">
        <button
          type="button"
          onClick={moveRight}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
          title="Adicionar"
        >
          <ChevronRight className="w-4 h-4" /> Adicionar
        </button>
        <button
          type="button"
          onClick={moveLeft}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300"
          title="Remover"
        >
          <ChevronLeft className="w-4 h-4" /> Remover
        </button>
      </div>

      {/* Direita */}
      <div>
        <div className="mb-2 font-medium text-sm text-gray-700">
          {titleRight}
        </div>
        <input
          className="w-full mb-2 border rounded px-2 py-1"
          placeholder="Filtrar..."
          value={filterRight}
          onChange={(e) => setFilterRight(e.target.value)}
        />
        <select
          multiple
          className="w-full h-48 border rounded p-2 bg-white"
          value={pickedRight}
          onChange={
            (e) =>
              setPickedRight(
                Array.from(e.target.selectedOptions, (o) => String(o.value))
              ) // 🔧 string
          }
        >
          {rightList.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
